/**
 * src/ui/share-modal.js — modal unificado de compartir (27-may-2026).
 *
 * Extraído de src/core/groups.js openInviteModal (Bloque 8.5) para que
 * pueda reusarse desde:
 *   - Header del grupo (Invitar amigos) → openInviteModal sigue como wrapper.
 *   - Despliegue de arenga (grupo o Tribuna) → openShareModal directo.
 *   - Otros consumers de navigator.share (pacto, otros rituales) que el
 *     dueño decida migrar (DT-DL scope reducido).
 *
 * Firma genérica: openShareModal({ title, subtitle, shareUrl, shareText })
 *   - title:    h3 del modal (ej "Compartir arenga · Festejo").
 *   - subtitle: línea tenue debajo del título (ej grupo + código, o nombre
 *               del creador). Opcional.
 *   - shareUrl: URL absoluta que se copia / lleva a WhatsApp / Telegram.
 *   - shareText: texto pre-cargado en WhatsApp / Telegram. Si no se pasa,
 *               se usa solo la URL.
 *
 * Botones fijos: WhatsApp, Telegram, Copiar link, Cancelar.
 * Cierre: X / click overlay / ESC / Cancelar.
 *
 * Razones del modal propio (mantenidas del Bloque 8.5):
 *   - Desktop Edge/Chrome Win: navigator.share solo muestra apps registradas
 *     en Windows (Outlook). No expone WhatsApp Web ni Telegram Web.
 *   - Mobile Android: navigator.share se cierra al tocar "más opciones".
 */

let _escHandler = null;

export const closeShareModal = () => {
    if (typeof document === 'undefined') return;
    const m = document.getElementById('share-modal');
    if (m) m.remove();
    if (_escHandler) {
        document.removeEventListener('keydown', _escHandler);
        _escHandler = null;
    }
};

// HTML puro del modal — exportado para test (sin DOM).
export const buildShareModalHTML = ({ title, subtitle, shareUrl }) => {
    const t = String(title || 'Compartir');
    const s = subtitle ? `<p class="share-modal-sub">${escapeHtml(String(subtitle))}</p>` : '';
    return `
        <div id="share-modal" class="share-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="share-modal-title">
            <style>
                .share-modal-overlay { position: fixed; inset: 0; z-index: 9999; background: rgba(0,0,0,.72); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; padding: 1rem; }
                .share-modal-card { width: 100%; max-width: 380px; background: var(--glass-bg); border: 1px solid var(--glass-border); border-radius: 16px; padding: 1.5rem 1.25rem; box-shadow: 0 8px 32px rgba(0,0,0,.4); animation: shareModalIn .3s ease-out forwards; }
                @keyframes shareModalIn { from { opacity: 0; transform: scale(.97); } to { opacity: 1; transform: scale(1); } }
                .share-modal-card h3 { font-family: var(--font-heading); color: var(--white); font-size: 1.1rem; text-align: center; margin: 0 0 .4rem; overflow-wrap: anywhere; }
                .share-modal-sub { color: var(--text-muted); font-size: .78rem; text-align: center; margin: 0 0 1.25rem; }
                .share-modal-btn { width: 100%; min-height: 48px; border: 0; border-radius: 10px; font-weight: 700; font-size: .92rem; cursor: pointer; margin-bottom: .7rem; display: flex; align-items: center; justify-content: center; gap: .55rem; padding: .6rem 1rem; transition: opacity .2s ease; }
                .share-modal-btn:hover { opacity: .9; }
                .share-modal-btn-wa { background: #25D366; color: #0a3622; }
                .share-modal-btn-tg { background: #229ED9; color: #fff; }
                .share-modal-btn-copy { background: rgba(255,255,255,.08); color: var(--white); border: 1px solid var(--glass-border); }
                .share-modal-btn-cancel { background: transparent; color: var(--text-muted); border: 0; min-height: 36px; font-size: .82rem; margin-top: .3rem; width: 100%; cursor: pointer; }
            </style>
            <div class="share-modal-card">
                <h3 id="share-modal-title">${escapeHtml(t)}</h3>
                ${s}
                <button type="button" class="share-modal-btn share-modal-btn-wa" data-share="wa">📱 WhatsApp</button>
                <button type="button" class="share-modal-btn share-modal-btn-tg" data-share="tg">✈️ Telegram</button>
                <button type="button" class="share-modal-btn share-modal-btn-copy" data-share="copy">📋 Copiar link</button>
                <button type="button" class="share-modal-btn-cancel" data-share="cancel">Cancelar</button>
            </div>
        </div>
    `;
};

const escapeHtml = (s) => String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const openShareModal = ({ title, subtitle, shareUrl, shareText }) => {
    if (typeof document === 'undefined') return;
    closeShareModal();
    if (!shareUrl) return;
    // Si el shareText ya contiene la URL (caso invitación de grupo histórica),
    // no la duplicamos. Si no la contiene (caso arenga), la appendeamos.
    const textIncluyeUrl = shareText && shareText.includes(shareUrl);
    const textToShare = textIncluyeUrl ? shareText
        : (shareText ? `${shareText}\n${shareUrl}` : shareUrl);
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(textToShare)}`;
    // Telegram: separar `url=` (el link) del `text=` (contexto sin URL).
    // Si shareText incluye la URL, la sacamos del text para Telegram.
    const textTelegram = textIncluyeUrl ? shareText.replace(shareUrl, '').trim() : (shareText || '');
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(textTelegram)}`;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = buildShareModalHTML({ title, subtitle, shareUrl });
    const modal = wrapper.firstElementChild;
    document.body.appendChild(modal);

    // Click en overlay (fuera de la card) cierra.
    modal.addEventListener('click', (e) => { if (e.target === modal) closeShareModal(); });
    // ESC cierra.
    _escHandler = (e) => { if (e.key === 'Escape') closeShareModal(); };
    document.addEventListener('keydown', _escHandler);

    modal.querySelector('[data-share="cancel"]')?.addEventListener('click', closeShareModal);
    modal.querySelector('[data-share="wa"]')?.addEventListener('click', () => {
        window.open(whatsappUrl, '_blank');
        closeShareModal();
    });
    modal.querySelector('[data-share="tg"]')?.addEventListener('click', () => {
        window.open(telegramUrl, '_blank');
        closeShareModal();
    });

    const btnCopy = modal.querySelector('[data-share="copy"]');
    btnCopy?.addEventListener('click', async () => {
        let ok = false;
        if (navigator.clipboard?.writeText) {
            try { await navigator.clipboard.writeText(textToShare); ok = true; } catch { /* fallback */ }
        }
        if (!ok) {
            try {
                const ta = document.createElement('textarea');
                ta.value = textToShare;
                ta.style.position = 'fixed';
                ta.style.top = '-1000px';
                document.body.appendChild(ta);
                ta.select();
                ok = document.execCommand('copy');
                document.body.removeChild(ta);
            } catch { /* fallback final */ }
        }
        if (!ok) {
            try { alert(textToShare); } catch { /* sin alert */ }
            return;
        }
        const original = btnCopy.textContent;
        btnCopy.textContent = 'Copiado ✓';
        btnCopy.disabled = true;
        setTimeout(() => { btnCopy.textContent = original; btnCopy.disabled = false; }, 2000);
    });
};

// Bridge a window para invocaciones desde onclick="" inline.
if (typeof window !== 'undefined') {
    window.openShareModal = openShareModal;
    window.closeShareModal = closeShareModal;
}
